import { Injectable } from '@angular/core';

/** A completed Stripe checkout, reported to Google Analytics as a `purchase`. */
export interface IPurchaseEvent {
    /** Unique per checkout (the Stripe Checkout Session ID), so a purchase is never counted twice. */
    transactionId: string;
    value: number;
    currency: string;
    planName: string;
}

/** What happened with the sidekick, reported as the `action` of a `sidekick` event. */
export type SidekickAction = 'shown' | 'cta_click' | 'close' | 'mute' | 'unmute' | 'hide' | 'name' | 'rename' | 'name_skip';

/** Subscription offered on the paywall: yearly or monthly. */
export type PaywallPlan = 'year' | 'month';

type Gtag = (command: 'event', name: string, params: Record<string, unknown>) => void;

/** Transaction IDs already reported from this browser. */
const TRACKED_PURCHASES_KEY = 'saideline_tracked_purchases';
const TRACKED_PURCHASES_LIMIT = 20;

/**
 * Sends conversion events to Google Analytics 4. Google Ads imports them from GA4
 * (`purchase` drives the Sales campaign). Events are only sent when the visitor accepted
 * analytics cookies: without consent the Google tag is not loaded and nothing is sent.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    /** A new account was registered. */
    trackSignUp(): void {
        this.send('sign_up', { method: 'email' });
    }

    /** The paywall was shown. @param source - Why, e.g. `credits_exhausted` or `direct_link`. */
    trackPaywallView(source: string): void {
        this.send('paywall_view', { source });
    }

    /** The coach picked another subscription on the paywall. */
    trackPlanSelect(plan: PaywallPlan): void {
        this.send('plan_select', { plan });
    }

    /** The coach clicked a subscribe button, sent before the redirect to Stripe. @param value - Price in EUR. */
    trackBeginCheckout(plan: PaywallPlan, value: number): void {
        this.send('begin_checkout', { plan, value, currency: 'EUR' });
    }

    /** The coach followed the invite link on the paywall. */
    trackReferralClick(): void {
        this.send('referral_click', {});
    }

    /** The coach closed the paywall ("Later" or the close button). */
    trackPaywallDismiss(): void {
        this.send('paywall_dismiss', {});
    }

    /**
     * The sidekick did something: showed a message, the coach clicked its button, closed it, muted or hid it.
     * @param message - Which message (`firstVisit`, `session1` … `upgrade`), when it is about one
     */
    trackSidekick(action: SidekickAction, message?: string): void {
        this.send('sidekick', message ? { action, message } : { action });
    }

    /** A new paid subscription was started through Stripe Checkout. */
    trackPurchase(purchase: IPurchaseEvent): void {
        const tracked = this.getTrackedPurchases();
        if (tracked.includes(purchase.transactionId)) {
            return;
        }

        const sent = this.send('purchase', {
            transaction_id: purchase.transactionId,
            value: purchase.value,
            currency: purchase.currency,
            items: [
                {
                    item_id: purchase.planName,
                    item_name: purchase.planName,
                    price: purchase.value,
                    quantity: 1,
                },
            ],
        });

        if (sent) {
            this.setTrackedPurchases([...tracked, purchase.transactionId].slice(-TRACKED_PURCHASES_LIMIT));
        }
    }

    /** @returns Whether the event was handed to the Google tag. */
    private send(name: string, params: Record<string, unknown>): boolean {
        const gtag = (window as unknown as { gtag?: Gtag }).gtag;
        if (typeof gtag !== 'function') {
            return false;
        }

        gtag('event', name, params);
        return true;
    }

    private getTrackedPurchases(): string[] {
        try {
            const parsed = JSON.parse(localStorage.getItem(TRACKED_PURCHASES_KEY) ?? '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private setTrackedPurchases(ids: string[]): void {
        try {
            localStorage.setItem(TRACKED_PURCHASES_KEY, JSON.stringify(ids));
        } catch {
            // Storage unavailable: GA4 still deduplicates purchases by transaction_id.
        }
    }
}
